import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { notify } from "~/utils/helper";

import classNames from 'classnames/bind';
import styles from './FormUploadFile.module.scss';
interface FormUploadFileProps {
  onChange: (data: any) => void;
  url?: string;
  errorMessage?: string;
  isViewMode?: boolean;
}
type FormUploadFileRef = {
  opendUploadFile: () => void;
}

const cx = classNames.bind(styles);

const FormUploadFile = forwardRef<FormUploadFileRef, FormUploadFileProps>(({
  onChange,
  errorMessage,
  url,
  isViewMode,
}, ref) => {

  useImperativeHandle(ref, () => ({
    opendUploadFile() {
      openFileDialog();
    },
  }));

  const [formData, setFormData] = useState({
    fileSize: "",
    fileName: "",
    description: "",
    file: null as File | null,
    fileType: "",
    title: '',
    fileUrl: '' as String | undefined,
  });
  const [error, setError] = useState({
    description: "",
    file: "",
  });
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);


  const formatFileSize = (size: number) => {
    if (size >= 1024 ** 3) return `${(size / 1024 ** 3).toFixed(2)} GB`;
    if (size >= 1024 ** 2) return `${(size / 1024 ** 2).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${size} B`;
  };

  const allowedTypes = {
    "application/pdf": "pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
  };

  const handleFileSelect = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      notify("error", "File size must be less than 10MB");
      return;
    }

    // Check file extension
    const extension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg"];

    if (!extension || !allowedExtensions.includes(extension)) {
      notify("error", "File type not supported. Allowed types: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG");
      return;
    }

    setFormData({
      ...formData,
      file,
      fileName: `${file.name}`,
      fileSize: formatFileSize(file.size),
      fileType: extension,
      fileUrl: url,
    })

    onChange({
      file,
      fileName: `${file.name}`,
      fileSize: formatFileSize(file.size),
      fileType: extension,
      fileUrl: url,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };


  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  return (<>
    <div>
      <div
        className={cx("", {
          "error-upload": error.file !== "",
          "drag-active": isDragActive,
        })}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {formData.file ? (
          <div className={cx('preview')}>
            <p className={cx('file-info')}>
              {formData.fileName} (
              {formData.fileSize})
            </p>
            <div className={cx('divider')}></div>
            <div className={cx('btn-remove')} onClick={openFileDialog}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.75 18.75H16.75C17.8546 18.75 18.75 17.8546 18.75 16.75V2.75C18.75 1.64543 17.8546 0.75 16.75 0.75L2.75 0.75C1.64543 0.75 0.75 1.64543 0.75 2.75L0.75 16.75C0.75 17.8546 1.64543 18.75 2.75 18.75ZM2.75 18.75L13.75 7.75L18.75 12.75M7.75 6.25C7.75 7.07843 7.07843 7.75 6.25 7.75C5.42157 7.75 4.75 7.07843 4.75 6.25C4.75 5.42157 5.42157 4.75 6.25 4.75C7.07843 4.75 7.75 5.42157 7.75 6.25Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <p>Change</p>
            </div>
          </div>
        ) : (
          <div className={cx("upload-section", {
            "error-upload": error.file !== "",
            "drag-active": isDragActive,
          })}>
            <p className={cx("upload-text")}>
              Drag and drop your file here or <span className={cx('click-upload')} onClick={openFileDialog}>Click to upload</span>
            </p>
            <p className={cx("upload-description")}>Maximum file size: 10MB</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,.xlsx"
          style={{ display: "none" }}
          id="file-upload"
          onChange={handleFileChange}
        />
      </div>
      {error.file && <p className={cx("error-message")}>{error.file}</p>}
    </div>
  </>)
});

export default FormUploadFile;